import Link from "@/components/feature/LocaleLink";
import PageLayout from "@/components/feature/PageLayout";
import { useLang } from "@/lib/useLang";
import { SATELLITE } from "@/data/satellite";
import { SOLUTIONS } from "@/data/solutions";
import imgVsat from "@/assets/images/site/satcom.jpg";
import imgShip from "@/assets/images/business-satellite.jpg";
import imgRouter from "@/assets/images/site/vsat-check.jpg";

const SectionTitle = ({ children }: { children: string }) => (
  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">{children}</h2>
);

const RELATED = ["citadel", "gps-receiver", "it-equipment"];

export default function SatellitePage() {
  const lang = useLang();
  const s = SATELLITE[lang];
  const related = SOLUTIONS.filter((x) => RELATED.includes(x.slug));

  return (
    <PageLayout eyebrow={s.eyebrow} title={s.title} desc={s.desc} seoTitle={s.seoTitle} seoDescription={s.seoDescription}>
      <div className="space-y-20">
        {/* 사진 띠 */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-reveal data-reveal-stagger>
          <img src={imgVsat} alt={lang === "ko" ? "선박 VSAT 위성통신 안테나" : "Vessel VSAT satellite antenna"} fetchPriority="high" className="w-full aspect-[4/3] object-cover rounded-lg md:col-span-1" />
          <img src={imgShip} alt={lang === "ko" ? "위성통신 장비를 갖춘 선박" : "Vessel equipped with satellite communication"} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover rounded-lg" />
          <img src={imgRouter} alt={lang === "ko" ? "선박 위성통신 안테나 점검" : "Inspecting a vessel satellite antenna"} loading="lazy" decoding="async" className="w-full aspect-[4/3] object-cover rounded-lg" />
        </section>

        {/* 통신 시스템 */}
        <section id="systems">
          <SectionTitle>{s.systemsTitle}</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-reveal data-reveal-stagger>
            {s.systems.map((x) => (
              <div key={x.title} className="glow-card glow-card-light p-5 rounded-lg bg-background-100 border border-background-200">
                <div className="w-11 h-11 flex items-center justify-center rounded-md bg-primary-500 text-background-50 mb-4">
                  <i className={`${x.icon} text-lg`}></i>
                </div>
                <h3 className="font-heading font-bold text-foreground-950 mb-1">{x.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{x.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 서비스 범위 */}
        <section id="scope">
          <SectionTitle>{s.serviceTitle}</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-reveal data-reveal-stagger>
            {s.services.map((x) => (
              <div key={x.title} className="glow-card flex gap-4 p-5 rounded-lg bg-foreground-950 text-background-50">
                <i className={`${x.icon} text-2xl text-accent-300 shrink-0`}></i>
                <div>
                  <h3 className="font-heading font-semibold mb-1">{x.title}</h3>
                  <p className="text-sm text-foreground-300 leading-relaxed">{x.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 커미셔닝 프로세스 */}
        <section id="process">
          <SectionTitle>{s.processTitle}</SectionTitle>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" data-reveal data-reveal-stagger>
            {s.process.map((p, i) => (
              <li key={p.title} className="glow-card glow-card-light p-5 rounded-lg bg-background-100 border border-background-200">
                <div className="font-heading text-3xl font-bold text-background-300 mb-3">0{i + 1}</div>
                <h3 className="font-heading font-semibold text-foreground-950 mb-2">{p.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{p.desc}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* 실적 · 이메일 */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <SectionTitle>{s.recordTitle}</SectionTitle>
            <ul className="space-y-3">
              {s.records.map((r) => (
                <li key={r} className="flex items-start gap-2 text-foreground-700 leading-relaxed">
                  <i className="ri-check-line text-accent-600 mt-1"></i>
                  {r}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5 p-6 rounded-lg bg-primary-50 border border-primary-200">
            <i className="ri-mail-send-line text-2xl text-primary-600"></i>
            <h3 className="font-heading text-xl font-bold text-foreground-950 mt-3 mb-2">{s.emailTitle}</h3>
            <p className="text-sm text-foreground-700 leading-relaxed">{s.emailDesc}</p>
            <Link to="/solutions/email-sharing" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
              {lang === "ko" ? "선박 이메일 공유 OCEAN-PublicMail™ 자세히 보기" : "Learn about vessel email sharing — OCEAN-PublicMail™"}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>

        {/* 관련 솔루션 */}
        <section>
          <SectionTitle>{s.relatedTitle}</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/solutions/${r.slug}`}
                className="group flex items-center gap-4 p-5 rounded-lg bg-background-100 border border-background-200 hover:border-primary-400 transition-colors cursor-pointer"
              >
                <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className={`${r.icon} text-lg`}></i>
                </div>
                <span className="flex-1 font-heading font-semibold text-foreground-950">{r.text[lang].title}</span>
                <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all"></i>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </PageLayout>
  );
}
